import { useState } from "react";
import { Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { openCookieSettings } from "./CookieConsent";


const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("submit-contact-message", {
        body: {
          name: formData.name,
          email: formData.email,
          message: formData.message,
        },
      });

      if (error || (data && (data as { error?: string }).error)) {
        throw new Error("send failed");
      }

      toast({
        title: "Nachricht gesendet!",
        description: "Wir melden uns schnellstmöglich bei dir.",
      });
      setFormData({ name: "", email: "", message: "" });
    } catch {
      toast({
        title: "Fehler",
        description: "Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-4 bg-secondary/30 border-t border-border/30">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-12">
          Kontakt
        </h2>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="flex flex-col items-center gap-3">
            <MessageCircle className="w-6 h-6 text-primary/70" />
            <a href="https://wa.me/491742022890" target="_blank" rel="noopener noreferrer" className="font-[Montserrat] text-sm text-foreground/80 hover:text-primary transition-colors">WhatsApp</a>
            <span className="font-[Montserrat] text-xs text-muted-foreground">0174 2022890</span>
          </div>
          <div className="flex flex-col items-center gap-3">
            <Mail className="w-6 h-6 text-primary/70" />
            <a href="mailto:info@luther-kl.com" className="font-[Montserrat] text-sm text-foreground/80 hover:text-primary transition-colors">info@luther-kl.com</a>
          </div>
          <div className="flex flex-col items-center gap-3">
            <MapPin className="w-6 h-6 text-primary/70" />
            <span className="font-[Montserrat] text-sm text-foreground/80">
              Martin-Luther-Straße 8<br />67657 Kaiserslautern
            </span>
          </div>
        </div>

        {/* Contact Form */}
        <form onSubmit={handleSubmit} className="max-w-lg mx-auto mb-12 space-y-4 text-left">
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              className="w-full px-4 py-3 bg-card border border-border rounded text-foreground placeholder:text-muted-foreground font-[Montserrat] text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors"
            />
            <input
              type="email"
              placeholder="E-Mail"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
              className="w-full px-4 py-3 bg-card border border-border rounded text-foreground placeholder:text-muted-foreground font-[Montserrat] text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors"
            />
          </div>
          <textarea
            placeholder="Deine Nachricht…"
            value={formData.message}
            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            required
            rows={4}
            className="w-full px-4 py-3 bg-card border border-border rounded text-foreground placeholder:text-muted-foreground font-[Montserrat] text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 transition-colors resize-none"
          />
          <div className="text-center">
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded font-[Montserrat] text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              {sending ? "Wird gesendet…" : "Nachricht senden"}
            </button>
          </div>
        </form>

        <div className="border-t border-border pt-8 space-y-4">
          <p className="text-foreground/70 text-base">
            Reservierungen per E-Mail oder WhatsApp.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/impressum" className="font-[Montserrat] text-xs tracking-[0.15em] uppercase text-foreground/40 hover:text-primary transition-colors">
              Impressum
            </Link>
            <span className="text-foreground/20">|</span>
            <Link to="/datenschutz" className="font-[Montserrat] text-xs tracking-[0.15em] uppercase text-foreground/40 hover:text-primary transition-colors">
              Datenschutz
            </Link>
            <span className="text-foreground/20">|</span>
            <button
              type="button"
              onClick={openCookieSettings}
              className="font-[Montserrat] text-xs tracking-[0.15em] uppercase text-foreground/40 hover:text-primary transition-colors"
            >
              Cookie-Einstellungen
            </button>
          </div>


          <p className="font-[Montserrat] text-xs text-muted-foreground tracking-wider uppercase">
            © {new Date().getFullYear()} Luther Club Kaiserslautern. Alle Rechte vorbehalten.
          </p>
          <p className="font-[Montserrat] text-xs text-muted-foreground tracking-wider">
            In Liebe umgesetzt von{" "}
            <a
              href="https://msproduction-kl.de"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/60 hover:text-primary transition-colors"
            >
              MS-Production
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Contact;
